/**
 * MCP Apps barrel — v1.2+
 *
 * Registers all UI-enabled tools and `ui://` HTML resources on every session.
 * Tools carry `_meta.ui.resourceUri` per the MCP Apps spec; MCP-Apps-capable
 * clients (Claude Desktop, Claude.ai with MCP Apps support) render the
 * associated `ui://` resource as a sandboxed iframe. Clients that don't
 * speak MCP Apps simply ignore the `_meta.ui` metadata and call the tool
 * as a normal tool with structured output.
 *
 * Per-app tier gates live INSIDE each register*() function. The previous
 * version of this barrel gated the whole registration block on
 * `getUiCapability` from the client's `initialize` capabilities — that
 * was overly aggressive and hid the tools from clients (including
 * Claude Code) that don't yet advertise `io.modelcontextprotocol/ui`
 * but can still execute structured-output tools.
 *
 * Sub-phase ownership:
 *   v1.2a — fleet-health-app
 *   v1.2b — provisioning-wizard
 *   v1.2d — balance-topup-form
 */

import type { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import type { ToolContext } from "../tools.js";
import { registerFleetHealthApp } from "./fleet-health-app.js";
import { registerProvisioningWizard } from "./provisioning-wizard.js";
import { registerBalanceTopupApp } from "./balance-topup.js";

export {
  registerFleetHealthApp,
  registerProvisioningWizard,
  registerBalanceTopupApp,
};

/**
 * Register all MCP App tools and resources.
 *
 * Always registers unconditionally — tier gates are enforced inside each
 * register*() function. MCP-Apps-capable clients render the iframes;
 * other clients see the tools as normal structured-output tools.
 */
export function registerAllApps(server: McpServer, ctx: ToolContext): void {
  // v1.2a — Fleet Health Dashboard (free+, read scope)
  registerFleetHealthApp(server, ctx);

  // v1.2b — eSIM Provisioning Wizard (pro+, write scope; tier-gated internally)
  registerProvisioningWizard(server, ctx);

  // v1.2d — Balance Top-up Form (enterprise only, admin scope; tier-gated internally)
  registerBalanceTopupApp(server, ctx);
}
